<template>
	<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-12">
			<label for="sourceLocatorId">{{$t('Squeeze.Locators.Tabs.SourceLocator')}} *</label>
			<Dropdown id="sourceLocatorId" v-model="value.sourceLocatorId" :options="allLocators" :loading="loading" :class="{'p-invalid':v$.sourceLocatorId.$invalid && showErrorMessage}" :filter="true" :autoFilterFocus="true" optionValue="id" :optionLabel="getTranslatedDescription" @change="update" @show="showDropdownOverlay" />
			<small v-if="v$.sourceLocatorId.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Imports.Protocol')}) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="dbcolumn">{{$t('Squeeze.Locators.Tabs.DBColumn')}} *</label>
			<Dropdown id="dbcolumn" v-model="value.dbcolumn" :options="masterDataColumns" :loading="loading" :class="{'p-invalid':v$.dbcolumn.$invalid && showErrorMessage}" :filter="true" :autoFilterFocus="true" optionValue="id" :optionLabel="getTranslatedDescription" @change="update" @show="showDropdownOverlay" />
			<small v-if="v$.dbcolumn.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Imports.Encryption')}) }}
			</small>
		</div>
		<div class="p-field p-col-12">
			<label for="stopUnique">{{$t('Squeeze.Locators.Tabs.Stop')}}</label>
			<Dropdown id="stopUnique" v-model="value.stopIfUnique" :options="stopUnique" :loading="loading" :class="{'p-invalid':v$.stopIfUnique.$invalid && showErrorMessage}" optionValue="id" optionLabel="name" @change="update"  />
			<small v-if="v$.stopIfUnique.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Locators.Tabs.Stop')}) }}
			</small>
		</div>
	</div>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {computed, defineComponent, onMounted, PropType, reactive, ref, watch} from "vue";
import Dropdown from 'primevue/dropdown';
import {DbLinkLocatorDetails, DocumentLocator, MasterDataColumn} from "@dex/squeeze-client-ts";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {showDropdownOverlay} from "@/util/StylesHelper";
import {FormState} from "@/util/Types";

type DbLinkLocatorDetailsFormState = FormState<DbLinkLocatorDetails, "sourceLocatorId" | "dbcolumn" | "stopIfUnique">;

export default defineComponent({
	name: "DBLinkedDataForm",
	components: {
		Dropdown,
	},
	props: {
		DBLinkedData: {
			type: Object as PropType<DbLinkLocatorDetails>,
			required: true,
		},
		allLocators: {
			type: Array as PropType<DocumentLocator[]>,
			required: true,
		},
		masterDataColumns: {
			type: Array as PropType<MasterDataColumn[]>,
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
		},
	},
	emits: ['update'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Show loading? */
		const loading = ref(false);

		/** List of stopUnique booleans */
		const stopUnique = computed(() => {
			return [
				{
					id: true,
					name: t('Squeeze.General.Yes'),
				},
				{
					id: false,
					name: t('Squeeze.General.No'),
				},
			]
		});

		/** Current Object of all input-fields */
		const value = reactive<DbLinkLocatorDetailsFormState>({
			sourceLocatorId: 0,
			dbcolumn: 0,
			stopIfUnique: true,
		});

		/** Determines the required rules for validation */
		const rules = {
			sourceLocatorId: { required },
			dbcolumn: { required },
			stopIfUnique: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		onMounted(async () => {
			Object.assign(value, props.DBLinkedData);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.DBLinkedData, () => {
			Object.assign(value, props.DBLinkedData);
		})

		/** Triggered on change of any field */
		const update = () => {
			emit("update", value, v$.value.$invalid);
		}

		return {
			getTranslatedDescription,
			t,
			toast,
			loading,
			stopUnique,
			value,
			props,
			v$,
			Dropdown,
			update,
			showDropdownOverlay,
		}
	},
});

</script>

<style scoped>

</style>
